import { Fragment, useMemo } from 'react';

import { AlertTriangleIcon } from './icons';

/**
 * Reusable scope picker — card 040.
 *
 * Replaces the inline-text `.scopes-grid` + `.scope-row` markup that
 * previously lived in both `routes/ApiKeys.jsx` (EditKeyModal) and
 * `components/MintKeyModal.jsx`.  Renders the `/oauth/scopes` catalog
 * as a vertical list, one row per scope, matching the Claude MCP
 * OAuth consent quality bar the user called out on 2026-06-08.
 *
 * Each row:
 *   - Left: checkbox.
 *   - Middle: bold human label on top, scope code (`tours:read`)
 *     muted underneath.
 *   - Right (optional): one-line `description` from the catalogue.
 *
 * Dangerous scopes (`admin:write` today, anything carrying the
 * "(DANGEROUS)" hint in the label otherwise) get a left-border
 * accent, a slightly tinted background, a warning glyph next to
 * the label, and a screen-reader-only "Dangerous — grants
 * administrative actions" announcement wired via `aria-describedby`.
 *
 * Security: this is presentation only.  Server-side `require_admin`
 * still gates every admin endpoint, so a user tampering with the
 * DOM to silently tick `admin:write` does not actually gain
 * privileges they didn't already have via their tenant role.
 *
 * Props:
 *   - scopes:    catalog rows (`[{ scope, label, description }]`).
 *                Pass an empty array (or omit) while loading; the
 *                component will render the loading state itself
 *                when `loading` is true.
 *   - selected:  Set<string> of selected scope codes.
 *   - onToggle:  (scope: string) => void, called when a row toggles.
 *   - loading:   boolean — show "Loading scopes…" placeholder.
 *   - disabled:  boolean — render every row as read-only (used by
 *                the locked embed preset in MintKeyModal).
 *   - showHint:  boolean — render the visibility/hierarchy hint
 *                paragraph above the list.  Defaults to true; the
 *                locked embed branch passes false because the hint
 *                doesn't apply to a read-only preset.
 *   - idPrefix:  string — used to namespace `aria-describedby`
 *                targets so two pickers in the same DOM (eg. mint
 *                vs. edit modal opened in sequence) don't collide.
 */
export function ScopePicker({
  scopes,
  selected,
  onToggle,
  loading = false,
  disabled = false,
  showHint = true,
  idPrefix = 'scope-picker',
}) {
  const rows = scopes || [];
  // Group resource families (tours, content, themes, …) so the
  // read/write pairs cluster visually.  The catalogue already ships
  // them in pair order — we just insert a gap when the family prefix
  // changes.
  const grouped = useMemo(() => {
    const out = [];
    let lastFamily = null;
    for (const row of rows) {
      const family = (row.scope || '').split(':')[0] || '';
      out.push({ ...row, _family: family, _newFamily: family !== lastFamily });
      lastFamily = family;
    }
    return out;
  }, [rows]);

  if (loading && rows.length === 0) {
    return (
      <div className="scope-picker scope-picker--loading">
        <p className="scope-picker__loading">Loading scopes…</p>
        <style>{SCOPE_PICKER_CSS}</style>
      </div>
    );
  }

  const dangerHintId = `${idPrefix}-danger-hint`;

  return (
    <div className="scope-picker">
      {showHint && (
        <p className="scope-picker__hint">
          Admins can manage these keys but can't see the secret value of
          an Owner's key — the owner is the only one who ever sees the
          plaintext.
        </p>
      )}
      <span id={dangerHintId} className="scope-picker__sr-only">
        Dangerous — grants administrative actions.
      </span>
      <ul className="scope-picker__list" role="list">
        {grouped.map((row, idx) => {
          const isDanger = isDangerousScope(row);
          const isChecked = selected?.has(row.scope) ?? false;
          const groupBreak = idx > 0 && row._newFamily;
          return (
            <Fragment key={row.scope}>
              {groupBreak && <li className="scope-picker__group-break" aria-hidden="true" />}
              <li
                className={`scope-picker__row${isDanger ? ' scope-picker__row--danger' : ''}${disabled ? ' scope-picker__row--disabled' : ''}`}
              >
                <label className="scope-picker__label">
                  <input
                    type="checkbox"
                    className="scope-picker__checkbox"
                    checked={isChecked}
                    disabled={disabled}
                    readOnly={disabled}
                    onChange={disabled ? undefined : () => onToggle(row.scope)}
                    aria-describedby={isDanger ? dangerHintId : undefined}
                  />
                  <span className="scope-picker__body">
                    <span className="scope-picker__title">
                      {isDanger && (
                        <span className="scope-picker__danger-glyph" aria-hidden="true">
                          <AlertTriangleIcon size={14} />
                        </span>
                      )}
                      <strong className="scope-picker__name">{row.label || row.scope}</strong>
                      <code className="scope-picker__code">{row.scope}</code>
                    </span>
                    {row.description && (
                      <span className="scope-picker__desc">{row.description}</span>
                    )}
                  </span>
                </label>
              </li>
            </Fragment>
          );
        })}
      </ul>
      <style>{SCOPE_PICKER_CSS}</style>
    </div>
  );
}

// `admin:write` is the only dangerous scope today.  We also pattern-
// match the "(DANGEROUS)" hint the backend catalogue uses in the
// label so a new dangerous scope added server-side picks up the
// styling without needing a dashboard change.
function isDangerousScope(row) {
  if (!row) return false;
  if (row.scope === 'admin:write') return true;
  if (row.dangerous === true) return true;
  if (typeof row.label === 'string' && row.label.includes('DANGEROUS')) return true;
  return false;
}

const SCOPE_PICKER_CSS = `
  .scope-picker { margin-top: 8px; }
  .scope-picker__hint {
    margin: 0 0 10px;
    padding: 8px 10px;
    background: var(--surface-1);
    border-left: 3px solid var(--brand-600, #4f46e5);
    border-radius: 6px;
    font-size: 12px;
    line-height: 1.4;
    color: var(--text-2, #334155);
  }
  .scope-picker__loading {
    margin: 0;
    padding: 12px;
    background: var(--surface-1);
    border-radius: 8px;
    font-size: 12px;
    color: var(--text-3);
  }
  .scope-picker__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .scope-picker__group-break {
    height: 8px;
  }
  .scope-picker__row {
    border: 1px solid var(--border-1);
    border-radius: 8px;
    background: var(--surface-0);
    transition: background-color .12s ease, border-color .12s ease;
  }
  .scope-picker__row:hover {
    background: var(--surface-1);
  }
  .scope-picker__row--disabled {
    opacity: 0.7;
  }
  .scope-picker__row--disabled:hover {
    background: var(--surface-0);
  }
  .scope-picker__row--danger {
    border-left: 3px solid var(--danger-700, #b91c1c);
    background: color-mix(in srgb, var(--danger-700, #b91c1c) 5%, var(--surface-0));
  }
  .scope-picker__row--danger:hover {
    background: color-mix(in srgb, var(--danger-700, #b91c1c) 9%, var(--surface-0));
  }
  .scope-picker__label {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 10px 12px;
    cursor: pointer;
    font-size: 13px;
  }
  .scope-picker__row--disabled .scope-picker__label {
    cursor: default;
  }
  .scope-picker__checkbox {
    margin: 2px 0 0;
    flex: 0 0 auto;
  }
  .scope-picker__body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1 1 auto;
  }
  .scope-picker__title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    line-height: 1.3;
  }
  .scope-picker__danger-glyph {
    display: inline-flex;
    align-items: center;
    color: var(--danger-700, #b91c1c);
  }
  .scope-picker__name {
    font-weight: 600;
    color: var(--text-1, #0f172a);
  }
  .scope-picker__code {
    font-size: 11px;
    color: var(--text-3, #64748b);
    background: var(--surface-1);
    padding: 1px 6px;
    border-radius: 4px;
  }
  .scope-picker__desc {
    font-size: 12px;
    color: var(--text-3, #64748b);
    line-height: 1.4;
  }
  .scope-picker__sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;
